import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataProductsDTO } from "@src/domain/dto/masterData";
import type { MasterDataProduct } from "@src/domain/entities/masterData";
import { GetMasterDataProductsUseCase } from "@src/application/useCases/masterData";
import { MasterDataProductRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataListQuery } from "./masterData/useMasterDataListQuery";

interface MasterDataProductsDataContextValue {
  brandKey: string;
  products: MasterDataProduct[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const MasterDataProductsDataContext =
  createContext<MasterDataProductsDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataProducts(): MasterDataProductsDataContextValue {
  const context = useContext(MasterDataProductsDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataProducts must be used within MasterDataProductsDataProvider",
    );
  }
  return context;
}

interface MasterDataProductsDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function MasterDataProductsDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<MasterDataProductsDataProviderProps>) {
  const getProductsUseCase = useMemo(
    () => new GetMasterDataProductsUseCase(new MasterDataProductRepository()),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getProductsUseCase.execute(
        new GetMasterDataProductsDTO({
          includeInactive: true,
          search: args.search,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getProductsUseCase],
  );

  const {
    items: products,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useMasterDataListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load master data products",
    cacheScope: "products",
    fetchPage,
  });

  const value = useMemo(
    (): MasterDataProductsDataContextValue => ({
      brandKey,
      products,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      products,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <MasterDataProductsDataContext.Provider value={value}>
      {children}
    </MasterDataProductsDataContext.Provider>
  );
}
